import { useCallback, useEffect, useRef, useState } from 'react';

export type UseInfiniteScrollArgs = {
  perPage?: number;
  rowsLength: number;
  disabled?: boolean;
};

export const useOnScreenObserver = (callback: () => void) => {
  const observer = useRef<IntersectionObserver>();
  const callbackRef = useRef(callback);

  callbackRef.current = callback;

  const loaderRef = useCallback(node => {
    if (observer.current) observer.current.disconnect();

    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting) {
        callbackRef.current?.();
      }
    });

    if (node) observer.current.observe(node);
  }, []);

  return loaderRef;
};

export const useInfiniteScroll = ({ perPage = 20, rowsLength, disabled = false }: UseInfiniteScrollArgs) => {
  const [ page, setPage ] = useState(1);
  const [ loading, setLoading ] = useState(false);
  const [ dataAmount, setDataAmount ] = useState<number>(perPage);
  const observer = useRef<IntersectionObserver>();

  const hasMore = rowsLength > dataAmount;

  useEffect(() => {
    if (page === 1) return;
    setLoading(true);
    setTimeout(() => {
      setDataAmount((prev) => prev + perPage);
      setLoading(false);
    }, 250);
  }, [ page, perPage ]);

  const loaderRef = useCallback(node => {
    if (loading || disabled) return;
    if (observer.current) observer.current.disconnect();

    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        setPage(p => p + 1);
      }
    });

    if (node) observer.current.observe(node);
  }, [ loading, disabled, hasMore ]);


  return { loaderRef, dataAmount, showLoader: hasMore && !disabled };
};
